import React, { useState, useEffect } from "react";
import classes from "./Balance.module.css";
import { Spinner } from "react-bootstrap";
import api, { withCompanyId } from "../../Components/API/Api";
import Swal from "sweetalert2";
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import crop from "../../Assets/repoort.png";
import verified from "../../Assets/verified.svg";
import { Undo2 } from "lucide-react";

function Balance({ isOpen, onClose, initialData }) {

  const [showModal, setShowModal] = useState(isOpen);

  const [rows, setRows] = useState([
    { accountId: "", accountDisplay: "", description: "", debit: "", credit: "", showDropdown: false }
  ]);
  const [reference, setReference] = useState("");
  const [loadingRef, setLoadingRef] = useState(false);
  const [transactionDate, setTransactionDate] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);

  useEffect(() => {
    setShowModal(isOpen);
  }, [isOpen]);

  // Fetch journal reference
  useEffect(() => {
    const fetchReference = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      setLoadingRef(true);
      try {
        const res = await api.get('/v1/generate-journal-code');
        if (res?.data) {
          const payload = res.data;
          const d =
            payload?.data ??
            payload?.code ??
            payload?.reference ??
            payload?.number ??
            payload?.data?.code;
          let ref = "";
          if (typeof d === "string" || typeof d === "number") {
            ref = d;
          } else if (d && typeof d === "object") {
            ref = d.code || d.reference || d.number || "";
          }
          setReference(ref ? String(ref) : "");
        }
      } catch (error) {
        console.error("Error fetching journal reference:", error);
      } finally {
        setLoadingRef(false);
      }
    };

    if (isOpen) fetchReference();
  }, [isOpen]);

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      setAccountsLoading(true);
      try {
        const res = await api.get('/v1/account');
        let raw = res?.data?.data ?? res?.data ?? [];
        if (Array.isArray(raw.data)) raw = raw.data;
        setAccounts(Array.isArray(raw) ? raw : []);
      } catch (err) {
        console.error("Error fetching accounts:", err);
        setAccounts([]);
      } finally {
        setAccountsLoading(false);
      }
    };

    if (isOpen) fetchAccounts();
  }, [isOpen]);

  // Populate rows with accounts
  useEffect(() => {
    if (isOpen && accounts.length > 0) {
      setRows(accounts.map(acc => ({
        accountId: acc.id,
        accountName: acc.gl_name ?? acc.name ?? "",
        accountCode: acc.gl_code ?? acc.code ?? "",
        description: "",
        debit: "",
        credit: "",
        showDropdown: false
      })));
    }
  }, [isOpen, accounts]);


  // Conditional rendering after all hooks
  if (!isOpen || !showModal) return null;

  // ---------- Handlers ----------
  const addLine = () => {
    setRows(prev => [
      ...prev,
      { accountId: "", accountDisplay: "", description: "", debit: "", credit: "", showDropdown: false },
    ]);
  };

  const duplicateRow = (index) => {
    setRows(prev => {
      const newRows = [...prev];
      const emptyRow = { accountId: "", accountDisplay: "", description: "", debit: "", credit: "", showDropdown: false };
      newRows.splice(index + 1, 0, emptyRow);
      return newRows;
    });
  };

  const removeRow = (index) => {
    setRows(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    setRows(prev => {
      const newRows = [...prev];
      const row = { ...newRows[index] };
      if (field === "debit") {
        row.debit = value;
        if (parseFloat(value) > 0) row.credit = "0";
      } else if (field === "credit") {
        row.credit = value;
        if (parseFloat(value) > 0) row.debit = "0";
      } else if (field === "account") {
        row.accountId = value?.id ?? "";
        row.accountDisplay = `${value?.gl_code ?? value?.code ?? ""} - ${value?.gl_name ?? value?.name ?? ""}`;
      } else if (field === "showDropdown") {
        row.showDropdown = value;
      } else {
        row[field] = value;
      }
      newRows[index] = row;
      return newRows;
    });
  };

  const totalDebit = rows.reduce((sum, row) => sum + parseFloat(row?.debit || 0), 0);
  const totalCredit = rows.reduce((sum, row) => sum + parseFloat(row?.credit || 0), 0);

  const handlePostBalance = async () => {
    if (!transactionDate) {
      Swal.fire({ icon: "warning", title: "Missing Date", text: "Please select a transaction date." });
      return;
    }

    const validRows = rows.filter(r => r.accountId != null);
    const totalD = validRows.reduce((sum, r) => sum + (parseFloat(r.debit) || 0), 0);
    const totalC = validRows.reduce((sum, r) => sum + (parseFloat(r.credit) || 0), 0);

    if (totalD !== totalC) {
      Swal.fire({ icon: "warning", title: "Imbalanced Entry", text: "Total Debit and Credit must be equal before posting." });
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        jv_number: reference,
        transaction_date: transactionDate,
        account_id: validRows.map(r => r.accountId),
        direction: validRows.map(r => (parseFloat(r.debit) > 0 ? 1 : 2)),
        amount: validRows.map(r => parseFloat(r.debit || r.credit || 0)),
        all_debit: totalDebit,
        all_credit: totalCredit,
      };

      const res = await api.post('/v1/journal/post-balance-brought-forward', withCompanyId(payload));

      Swal.fire({ icon: "success", title: "Success", text: res.data?.message || JSON.stringify(res.data) });
      setRows([{ accountId: "", accountDisplay: "", description: "", debit: "", credit: "", showDropdown: false }]);
      setTransactionDate("");
    } catch (error) {
      console.error("Post balance error:", error);
      const backendMsg = error.response?.data?.message;
      Swal.fire({ icon: "error", title: "Backend Error", text: backendMsg ? JSON.stringify(backendMsg, null, 2) : "Failed to post balance." });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrinter = () => window.print();

  // ---------- Render ----------
  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent}>
        <h3 className={classes.title}>Set Opening Balances</h3>

        <div className={classes.topRow}>
          <div className={classes.inputGroup}>
            <label>Journal Reference</label>
            <div className={classes.refContainer}>
              {loadingRef ? (
                <div className={classes.spinnerWrapper}><Spinner animation="border" size="sm" /></div>
              ) : (
                <input type="text" value={reference} readOnly placeholder="Fetching reference..." />
              )}
            </div>
          </div>
          <div className={classes.inputGroup}>
            <label>Date</label>
            <input type="date" value={transactionDate} onChange={e => setTransactionDate(e.target.value)} required />
          </div>
        </div>

        <table className={classes.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Account Name</th>
              <th>Account Code</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {accountsLoading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                  <div className={classes.spinnerWrapper}><div className={classes.spinner}></div></div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "20px", color: "#888" }}>
                  No rows available.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={index}>
                  <td style={{fontSize:"13px"}} >{index + 1}</td>
                  <td style= {{fontSize:"12px"}}>{row.accountName || "—"}</td>
                  <td style={{textAlign:'center',fontSize:"12px"}}>{row.accountCode || "—"}</td>

                  <td>
                    <input
                      type="number"
                      className={classes.debitInput}
                      value={row.debit}
                      onChange={(e) => handleChange(index, "debit", e.target.value)}
                      placeholder="0.00"
                      disabled={row.credit && parseFloat(row.credit) > 0}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className={classes.creditInput}
                      value={row.credit}
                      onChange={(e) => handleChange(index, "credit", e.target.value)}
                      placeholder="0.00"
                      disabled={row.debit && parseFloat(row.debit) > 0}
                    />
                  </td>
                  <td>
                    <button onClick={() => removeRow(index)} className={classes.removeBtn}>Remove</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>

        </table>

        <div className={classes.totalRow}>
          <div className={classes.totalBox}><span>Total Debit</span><strong>{totalDebit.toFixed(2)}</strong></div>
          <div className={classes.totalBox}><span>Total Credit</span><strong>{totalCredit.toFixed(2)}</strong></div>
        </div>

        <div className={classes.footerButtons}>
          <button className={classes.addLineBtn} onClick={addLine}><Undo2 size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />Reverse Entry</button>
          <button className={classes.resetBtn} onClick={() => setRows([{ accountId: "", accountDisplay: "", description: "", debit: "", credit: "", showDropdown: false }])}>Reset</button>
          <button className={classes.printBtn} onClick={handlePrinter}>Print</button>
          <button className={classes.submitBtn} onClick={handlePostBalance} disabled={isSaving}>
            {isSaving ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Spinner animation="border" size="sm" />Saving...</span> : "Post Balance"}
          </button>
        </div>

        <button className={classes.closeBtn} onClick={onClose}>×</button>
      </div>
    </div>
  );
}

export default Balance;
